import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Tabs, Tab, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, List, ListItem, ListItemText, Link, Tooltip } from '@mui/material';
import Layout from './Layout';
import OverviewTab from './OverviewTab';
import TextTab from './TextTab';
import VotesTab from './VotesTab';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark'; 
import hasFeatureAccess from './utils/featureAccess';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelIcon from '@mui/icons-material/Label';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';

function BillPage() {
  const { id } = useParams();
  const location = useLocation();
  const [value, setValue] = useState(0);
  const [billData, setBillData] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const userId = localStorage.getItem('user_id');
  const [organizationId, setOrganizationId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const navigate = useNavigate();
  const [editNoteId, setEditNoteId] = useState(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelInput, setLabelInput] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [labels, setLabels] = useState([]);
  const [deleteLabelDialogOpen, setDeleteLabelDialogOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState(null);
  
  useEffect(() => {
    if (!hasFeatureAccess('search')) {
      navigate('/access-denied');
    }
  }, [navigate]);

  useEffect(() => {
    fetchLabels();
  }
  , [id, organizationId]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        setOrganizationId(data.organization);
        
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [userId]);


  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleBookmarkClick = async () => {
    const action = isBookmarked ? 'delete' : 'add';
    const response = await fetch(`${process.env.REACT_APP_API_URL}/save-user-bill/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
      },
      body: JSON.stringify({ bill_id: id, user_id: userId, action: action }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    await response.json();
    setIsBookmarked(!isBookmarked);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    if (tab) {
      setValue(parseInt(tab, 10));
    }

    fetch(`${process.env.REACT_APP_API_URL}/bills/info/${id}/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        setBillData(data);
        
        setIsBookmarked(data.bill.tracking); 
      })
      .catch(error => console.error('Error fetching bill data:', error));
  }, [id, location.search, userId]);

  // Fetch notes when dialog opens
  const handleOpenNoteDialog = async () => {
    setNoteDialogOpen(true);
    if (id && organizationId) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-note?bill_id=${id}&organization_id=${organizationId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setNotes(data);
        } else {
          setNotes([]);
        }
      } catch (error) {
        setNotes([]);
      }
    }
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteText('');
    setEditNoteId(null);
  };

  const handleEditClick = (note) => {
    setEditNoteId(note.id);
    setNoteText(note.note_text);
    setNoteDialogOpen(true);
  };

  const handleNoteSubmit = async () => {
    if (!noteText.trim()) return;
    try {
      if (editNoteId) {
        // Update existing note
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-note/${editNoteId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
          body: JSON.stringify({
            note_text: noteText,
          }),
        });
        if (response.ok) {
          setNoteText('');
          setEditNoteId(null);
          handleOpenNoteDialog();
        }
      } else {
        // Create new note
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-note/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
          body: JSON.stringify({
            user_id: userId,
            organization: organizationId,
            bill: id,
            note_text: noteText,
          }),
        });
        if (response.ok) {
          setNoteText('');
          handleOpenNoteDialog();
        }
      }
    } catch (error) {
      // Optionally handle error
    }
  };

  // Delete note handlers
  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-note/${noteToDelete.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });
      if (response.ok || response.status === 204) {
        setDeleteDialogOpen(false);
        setNoteToDelete(null);
        // Refresh notes after delete
        handleOpenNoteDialog();
      }
    } catch (error) {
      // Optionally handle error
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  // Handler to open label dialog and fetch current labels
  const handleOpenLabelDialog = async () => {
    setLabelDialogOpen(true);
    await fetchLabels();
  };

  // Handler to close label dialog
  const handleCloseLabelDialog = () => {
    setLabelDialogOpen(false);
    setLabelInput('');
    setSelectedLabel(null);
  };

  // Fetch all labels for this bill/org
  const fetchLabels = async () => {
    if (!id || !organizationId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-label?bill_id=${id}&organization_id=${organizationId}`, {
        headers: {
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLabels(data);
        console.log(data);

      } else {
        setLabels([]);
      }
    } catch {
      setLabels([]);
    }
  };

  // Typeahead handler
  const handleLabelInputChange = async (event, value) => {
    setLabelInput(value);
    setLabelLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-label/typeahead?label=${encodeURIComponent(value)}&organization_id=${organizationId}`, {
        headers: {
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLabelOptions(data);
      } else {
        setLabelOptions([]);
      }
    } catch {
      setLabelOptions([]);
    }
    setLabelLoading(false);
  };

  // Add label handler
  const handleAddLabel = async () => {
    let labelToAdd = null;
    if (selectedLabel && selectedLabel.label) {
      labelToAdd = selectedLabel.label;
    } else if (labelInput && typeof labelInput === 'string') {
      labelToAdd = labelInput;
    }
    if (!labelToAdd) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-label/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
        body: JSON.stringify({
          bill: id,
          organization: organizationId,
          user: userId,
          label: labelToAdd,
        }),
      });
      if (response.ok) {
        setLabelInput('');
        setSelectedLabel(null);
        await fetchLabels();
      }
    } catch {}
  };

  // Delete label handlers
  const handleDeleteLabelClick = (label) => {
    setLabelToDelete(label);
    setDeleteLabelDialogOpen(true);
  };

  const handleDeleteLabelConfirm = async () => {
    if (!labelToDelete) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-label/${labelToDelete.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });
      if (response.ok || response.status === 204) {
        setDeleteLabelDialogOpen(false);
        setLabelToDelete(null);
        await fetchLabels();
      }
    } catch {}
  };

  const handleDeleteLabelCancel = () => {
    setDeleteLabelDialogOpen(false);
    setLabelToDelete(null);
  };


  const vote = billData?.bill?.vote_type ?? null;
  const bill = billData?.bill ?? null;
  return (
    <Layout>
      <Box sx={{ width: '100%' }}>
        {billData && (
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: 24,
              lineHeight: 1.5,
              fontWeight: 'bold',
            }}
          >
            <Tooltip title="Track This Bill">
              <IconButton onClick={handleBookmarkClick} style={{ verticalAlign: 'middle', marginRight: 8 }}>
                {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>

            {billData.bill.year_introduced} {' | '} {billData.bill.state} {billData.bill.bill_number} - {billData.bill.title} 
            <Tooltip title="Add Notes To This Bill">
              <IconButton onClick={handleOpenNoteDialog}><TextSnippetIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Add Labels To This Bill">
              <IconButton onClick={handleOpenLabelDialog}><LabelIcon /></IconButton>
            </Tooltip>
            {labels && labels.length > 0 && (
              <Box sx={{ marginTop: 1, marginLeft: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {labels.map((label, index) => {
                  const isDark = index % 2 === 0;
                  const backgroundColor = isDark ? '#0B7269' : '#CCFBF1';
                  const textColor = isDark ? '#FFFFFF' : '#0B7269';

                  return (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor,
                        color: textColor,
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {label.label}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Typography>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              navigate(`?tab=${newValue}`);
            }}
            aria-label="bill tabs"
          >
            <Tab label="Overview" />
            <Tab label="Text" />
            <Tab label="Votes" />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <OverviewTab id={id} vote={vote} bill={bill}/>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <TextTab id={id} />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <VotesTab id={id} />
        </TabPanel>
        {/* Note Dialog */}
        <Dialog open={noteDialogOpen} onClose={handleCloseNoteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Bill Notes</DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
          <List sx={{ mt: 0, pt: 0 }}>
              {notes && notes.length > 0 ? (
                notes.map((note, idx) => (
                  <ListItem key={idx} alignItems="flex-start">
                    <ListItemText
                      primary={note.note_text}
                      secondary={
                        <>
                          {note.user.first_name && note.user.last_name
                            ? `${note.user.first_name} ${note.user.last_name} (${note.user.email})`
                            : note.user.email}
                          <br />
                          <span style={{ fontSize: 12, color: '#888' }}>
                            {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                          </span>
                          {String(note.user.id) === String(userId) && (
                            <>
                              <br />
                              <Link
                                component="button"
                                variant="body2"
                                color="primary"
                                onClick={() => handleEditClick(note)}
                                sx={{ mt: 1, display: 'inline-block', mr: 2 }}
                              >
                                <EditIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Edit
                              </Link>
                              <Link
                                component="button"
                                variant="body2"
                                color="error"
                                onClick={() => handleDeleteClick(note)}
                                sx={{ mt: 1, display: 'inline-block' }}
                              >
                                <DeleteIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />

                                Delete
                              </Link>
                            </>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">No notes yet.</Typography>
              )}
            </List>
            <TextField
              label="Add a note"
              multiline
              minRows={2}
              fullWidth
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNoteDialog}>Close</Button>
            <Button onClick={handleNoteSubmit} variant="contained" disabled={!noteText.trim()}>Submit</Button>
          </DialogActions>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Are You Sure You Want To Delete?</DialogTitle>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={labelDialogOpen} onClose={handleCloseLabelDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add Labels</DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
          <Autocomplete
            freeSolo
            options={labelOptions}
            getOptionLabel={option => typeof option === 'string' ? option : option.label}
            inputValue={labelInput}
            onInputChange={(event, value, reason) => {
              setLabelInput(value);
              if (reason === 'input') handleLabelInputChange(event, value, reason);
            }}
            value={selectedLabel}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                setSelectedLabel({ label: newValue });
              } else {
                setSelectedLabel(newValue);
              }
            }}
            loading={labelLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type to search or add label"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {labelLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ mt: 2 }}
              />
            )}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleAddLabel}
            disabled={!(selectedLabel && selectedLabel.label) && !labelInput}
            sx={{ mb: 2 }}
          >
            Add Label
          </Button>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Current Labels:</Typography>
            <List>
              {labels && labels.length > 0 ? (
                labels.map((label) => (
                  <ListItem key={label.id} secondaryAction={
                    <Link
                      component="button"
                      color="error"
                      variant="body2"
                      onClick={() => handleDeleteLabelClick(label)}
                    >
                      Delete
                    </Link>
                  }>
                    <ListItemText primary={label.label} />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">No labels yet.</Typography>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLabelDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Label Confirmation Dialog */}
        <Dialog open={deleteLabelDialogOpen} onClose={handleDeleteLabelCancel}>
          <DialogTitle>Are You Sure You Want To Delete This Label?</DialogTitle>
          <DialogActions>
            <Button onClick={handleDeleteLabelCancel}>Cancel</Button>
            <Button onClick={handleDeleteLabelConfirm} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default BillPage;